import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { ISkinModeResolver } from 'tag/ts/tag/interfaces/model/ISkinModeResolver';
import { ISkinModeResolverArgument } from 'tag/ts/tag/interfaces/model/ISkinModeResolverArgument';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import { PlayerType } from 'tag/ts/tag/model/type/PlayerType';
import { SkinMode } from 'tag/ts/tag/model/type/SkinMode';

/**
 * @copyright © Paramount Streaming
 * @author Stan Gletski
 * @description Centralizes the "which skin engine renders the player" decision — previously
 * computed independently (and able to drift out of sync) in `TagView.playerType`,
 * `AviaDependencyManager`'s avia-ui import gate, and `TagUtilities`'s desktop-Skin import gate.
 * Owned and instantiated once by `TagModel`, same pattern as `AdUtilities`/`SiteUtilities`;
 * other consumers reach the instance via `tag.model.skinMode`. `TagView.playerType` shares this
 * class's static `resolvePlayerTypeFromDeviceSignals()` method directly, without needing an
 * instance, since it doesn't need the full resolution.
 *
 * This resolver only decides which *engine* renders the player (desktop Skin / avia-js-ui /
 * custom Mobile Skin / none) — whether a customization plugin (e.g. Mobile Web UI) also rides
 * along on top of avia-js-ui is a separate, independent question, decided in `TagModel.ts`
 * directly from `skinMode` + device capability, not encoded as its own `SkinMode` value.
 *
 * **Precedence** (`resolve()`, highest first — one private method per tier):
 * 1. `#resolveFromTopUrl()` — top-URL `?skinMode=` override, dev/QA only.
 * 2. `#resolveFromHostConfig()` — currently only `SKIN.enabled: false` (unambiguous "no Smart
 *    Tag skin at all," e.g. how ctv-app opts out today).
 * 3. `#resolveFromDefault()` — embedType + device capability + `SKIN.defaultSkin`, the same
 *    inputs each consumer used to re-derive on its own.
 *
 * **Resulting scenarios** (full reference table: `docs/tag/architecture.md`, "Skin Engine
 * Selection"):
 * - CTV/STB (`SKIN.enabled: false`) → `NONE` — host renders everything, Smart Tag renders nothing.
 * - Real desktop, default config → `DESKTOP` — Smart Tag's own Skin app (chromeless player).
 * - Real desktop, `SKIN.defaultSkin: true` → `AVIA_UI` regardless of device — explicit host
 *   opt-out of desktop Skin in favor of avia-js-ui's stock UI.
 * - Phone/tablet → `AVIA_UI`; the Mobile Web UI Tag plugin then separately decides per-brand
 *   customization (Pluto TV real, P+/CBS.com no-op placeholders) via its own `!isDesktop()` gate
 *   in `TagModel.ts`, not encoded here.
 * - `CUSTOM_MOBILE` → override-only, never a computed default — `skin_mobile/` is a scaffold and
 *   renders nothing yet.
 *
 * **Backward compatibility**: no new required host-facing config. `SKIN.enabled`/
 * `SKIN.defaultSkin` are read as inputs to tiers 2/3 above, exactly as hosts already set them
 * today (ctv-app: `enabled: false`; TN Web/core-ui: never set, defaulting to `enabled: true`) —
 * none of the three audited hosts needs to change anything for this centralization to preserve
 * their current runtime behavior.
 *
 * **`CUSTOM_MOBILE` is deliberately excluded from `#resolveFromDefault()`.** The custom Mobile
 * Skin app (`src/skin_mobile/`) has no real functionality yet — routing real mobile-web traffic
 * into it by default would be a production break, not a test. It's reachable only through the
 * override tier above until product deliberately flips real traffic over to it once the skin is
 * actually built out.
 */
export class SkinModeResolver extends AbstractTagClass implements ISkinModeResolver {

	/**
	 * @description Top-URL query param name for forcing a `SkinMode` during dev/QA — same
	 * top-URL-param pattern used throughout Smart Tag (`RuntimeVO.debug.*`,
	 * `NerdModeBootstrapResolver`).
	 */
	static readonly #SKIN_MODE_QUERY_PARAM = 'skinMode';

	/**
	 * @description Maps a `?skinMode=` URL token to a `SkinMode` value. Looked up only when the
	 * param is present — an unrecognized token falls back to `SkinMode.DESKTOP` in
	 * `#resolveFromTopUrl()`, not through to the next precedence tier.
	 */
	static readonly #SKIN_MODE_URL_TOKENS: { [ token: string ]: SkinMode } = {
		desktop: SkinMode.DESKTOP,
		avia: SkinMode.AVIA_UI,
		'custom-mobile': SkinMode.CUSTOM_MOBILE,
		none: SkinMode.NONE
	};

	constructor( argument: ISkinModeResolverArgument ) {
		super(
			{
				...argument,
				name: 'SkinModeResolver'
			}
		);
	}

	/**
	 * @description Computes CHROMELESS vs FULL purely from embed type, device capability, and
	 * the `SKIN.defaultSkin` override — the exact decision `TagView.playerType` has always made.
	 * Static (no instance state involved) so `TagView.playerType` and this class's own
	 * `#resolveFromDefault()` share one implementation and can never drift apart, without
	 * `TagView` needing a `SkinModeResolver` instance of its own.
	 *
	 * Deliberately never looks at `SKIN.enabled` — that guard belongs to `resolve()`, not to
	 * `playerType`. `playerType`'s contract has always been "CHROMELESS or FULL regardless of
	 * whether a Smart Tag skin is enabled at all," and changing that here would be a real
	 * behavior change for its other consumers (`AviaPlayerOptionsManager`, `ErrorManager`,
	 * `EmbedManager`, `PlayerUIPlugin`), not just a refactor.
	 * @param argument
	 * @param argument.embedType - `params.SYSTEM.embedType`
	 * @param argument.isDesktopCapability - `capabilities.isDesktop` (device-level, not host-declared)
	 * @param argument.defaultSkin - `params.SKIN.defaultSkin === true`
	 * @returns The resolved `PlayerType`
	 */
	static resolvePlayerTypeFromDeviceSignals( argument: {
		embedType: EmbedType;
		isDesktopCapability: boolean;
		defaultSkin: boolean;
	} ): PlayerType {
		const { embedType, isDesktopCapability, defaultSkin } = argument;

		switch ( embedType ) {
			case EmbedType.DESKTOP:
			case EmbedType.MOBILE_WEB:
				// device capability decides desktop vs mobile; defaultSkin can still force FULL
				// regardless of device
				if ( defaultSkin === true ) {
					return PlayerType.FULL;
				}
				return isDesktopCapability ? PlayerType.CHROMELESS : PlayerType.FULL;
			case EmbedType.HTML5:
				// CTV/STB — always chromeless; defaultSkin never applies here
				return PlayerType.CHROMELESS;
			default:
				return PlayerType.CHROMELESS;
		}
	}

	/**
	 * @description Resolves the `SkinMode` for this Tag instance's current params by trying
	 * each precedence tier in order. See class doc for the full reasoning.
	 *
	 * One exception to strict precedence: the top-URL override (tier 1) is refused when it
	 * would divert a pin-gated session away from `DESKTOP`. `SkinController#onPlayerReady()`
	 * is the only place `params.USER.pinGate` is ever enforced, and it only runs under
	 * `DESKTOP` — `AVIA_UI`/`CUSTOM_MOBILE`/`NONE` have no equivalent gate. The `?skinMode=`
	 * override is dev/QA-only (see class doc) and must not be able to silently strip PIN-gate
	 * enforcement from a real pin-gated production session. This has no effect on real
	 * mobile-web or CTV traffic, which never legitimately resolve to `DESKTOP` in the first
	 * place, nor on the documented QA workflow of forcing `AVIA_UI` on a desktop browser for
	 * non-pin-gated content.
	 * @returns {SkinMode} The resolved skin engine
	 */
	resolve = (): SkinMode => {
		const legitimateMode = this.#resolveFromHostConfig() ?? this.#resolveFromDefault();
		const overrideMode = this.#resolveFromTopUrl();

		if ( overrideMode === undefined || overrideMode === legitimateMode ) {
			return overrideMode ?? legitimateMode;
		}

		if ( this.params.USER.pinGate === true && legitimateMode === SkinMode.DESKTOP ) {
			return legitimateMode;
		}

		return overrideMode;
	};

	/**
	 * @description Tier 1 — top-URL `?skinMode=` override. Returns `undefined` (falls through to
	 * the next tier) only when the param is absent. A present-but-unrecognized token forces
	 * `SkinMode.DESKTOP` rather than falling through — an explicit `?skinMode=` intent that
	 * doesn't match a known token is treated as a QA typo, not silently ignored.
	 */
	#resolveFromTopUrl = (): SkinMode | undefined => {
		const urlToken = this.utils.URL.getQueryField( SkinModeResolver.#SKIN_MODE_QUERY_PARAM, this.runtimeVO?.top?.url );
		if ( !urlToken ) {
			return undefined;
		}
		return SkinModeResolver.#SKIN_MODE_URL_TOKENS[ urlToken.toLowerCase() ] ?? SkinMode.DESKTOP;
	};

	/**
	 * @description Tier 2 — host TagConfig override. `SKIN.enabled: false` is explicit,
	 * unambiguous host intent to go fully chromeless with no Smart Tag skin at all (how ctv-app
	 * opts out today). Returns `undefined` (falls through to the computed default) otherwise.
	 */
	#resolveFromHostConfig = (): SkinMode | undefined => {
		return this.params.SKIN.enabled === false ? SkinMode.NONE : undefined;
	};

	/**
	 * @description Tier 3 — computed default. Same signals every consumer of this decision used
	 * to re-derive independently before this resolver existed.
	 */
	#resolveFromDefault = (): SkinMode => {
		const playerType = SkinModeResolver.resolvePlayerTypeFromDeviceSignals(
			{
				embedType: this.params.SYSTEM.embedType,
				isDesktopCapability: this.isDesktop(),
				defaultSkin: this.params.SKIN.defaultSkin === true
			}
		);

		return playerType === PlayerType.CHROMELESS ? SkinMode.DESKTOP : SkinMode.AVIA_UI;
	};

}